import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { MagnifyingGlass } from 'phosphor-react'
import { useCrmSearch } from 'hooks/crm'
import { Muted, surface, textColor } from './ui'

const Wrap = styled.div`
    position: relative;
    flex: 1;
    min-width: 200px;
    max-width: 520px;
    input {
        width: 100%;
        padding: 8px 12px 8px 34px;
        border: 1px solid rgba(128, 128, 128, 0.4);
        border-radius: 8px;
        background: transparent;
        color: inherit;
    }
    .icon {
        position: absolute;
        left: 10px;
        top: 10px;
        opacity: 0.6;
    }
`

const Results = styled.ul`
    position: absolute;
    z-index: 1200;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    margin: 0;
    padding: 6px;
    list-style: none;
    background: ${surface};
    color: ${textColor};
    border: 1px solid rgba(128, 128, 128, 0.35);
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
    max-height: 60vh;
    overflow-y: auto;
    li.group {
        font-size: 0.7rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        opacity: 0.6;
        padding: 6px 8px 2px;
    }
    button {
        width: 100%;
        text-align: left;
        background: none;
        border: 0;
        color: inherit;
        padding: 6px 8px;
        border-radius: 6px;
        cursor: pointer;
    }
    button:hover,
    button:focus-visible {
        background: rgba(59, 130, 246, 0.15);
        outline: none;
    }
`

const GROUPS = [
    ['contact', 'Contacts'],
    ['account', 'Accounts'],
    ['deal', 'Deals'],
    ['activity', 'Activity'],
]

/**
 * Global CRM search. Titles and subtitles are rendered as plain text (never HTML).
 * @param {{ onSelect: (hit: import('utility/crm/types').CrmSearchHit) => void }} props
 */
export const CrmSearch = ({ onSelect }) => {
    const [text, setText] = useState('')
    const [open, setOpen] = useState(false)
    const { results, loading, error, active } = useCrmSearch(text)
    const wrapRef = useRef(null)

    useEffect(() => {
        const onDown = event => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false)
        }
        document.addEventListener('mousedown', onDown)
        return () => document.removeEventListener('mousedown', onDown)
    }, [])

    const pick = hit => {
        setOpen(false)
        setText('')
        onSelect(hit)
    }

    return (
        <Wrap ref={wrapRef}>
            <MagnifyingGlass className="icon" size={18} aria-hidden="true" />
            <input
                type="search"
                aria-label="Search contacts, accounts, deals and notes"
                placeholder="Search contacts, accounts, deals, notes…"
                value={text}
                onChange={event => {
                    setText(event.target.value)
                    setOpen(true)
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={event => {
                    if (event.key === 'Escape') setOpen(false)
                    if (event.key === 'Enter' && results[0]) pick(results[0])
                }}
            />
            {open && text.trim().length > 0 ? (
                <Results role="listbox" aria-label="Search results">
                    {!active ? (
                        <li>
                            <Muted>Type at least 2 characters</Muted>
                        </li>
                    ) : null}
                    {active && loading ? (
                        <li role="status">
                            <Muted>Searching…</Muted>
                        </li>
                    ) : null}
                    {active && error ? (
                        <li role="alert">
                            <Muted>{error.message}</Muted>
                        </li>
                    ) : null}
                    {active && !loading && !error && results.length === 0 ? (
                        <li>
                            <Muted>No matches</Muted>
                        </li>
                    ) : null}
                    {GROUPS.map(([kind, label]) => {
                        const hits = results.filter(r => r.Kind === kind)
                        if (!hits.length) return null
                        return (
                            <React.Fragment key={kind}>
                                <li className="group">{label}</li>
                                {hits.map(hit => (
                                    <li key={`${kind}-${hit.Id}`}>
                                        <button type="button" onClick={() => pick(hit)}>
                                            <div>{hit.Title}</div>
                                            {hit.Subtitle ? <Muted>{hit.Subtitle}</Muted> : null}
                                        </button>
                                    </li>
                                ))}
                            </React.Fragment>
                        )
                    })}
                </Results>
            ) : null}
        </Wrap>
    )
}
